import type { CreasePattern } from '@/origami/types';
import type { VertexTopology } from '@/origami/topology';

export interface MaekawaResult {
  vertex: number;
  degree: number;
  applicable: boolean;
  notApplicableReason?: 'boundary-vertex' | 'unassigned-creases' | 'degree-too-low';
  mountains: number;
  valleys: number;
  flats: number;
  unassigned: number;
  /** M - V. Must be +2 or -2. */
  difference: number;
  satisfied: boolean;
}

/**
 * Maekawa-Justin theorem (necessary condition for local flat-foldability).
 *
 *     |M - V| = 2
 *
 * at every interior vertex of a flat-folded crease pattern, where M and V count
 * the mountain and valley creases at that vertex.
 *
 * Two consequences worth surfacing in the UI: the degree of a flat-foldable
 * interior vertex must be even (M + V = degree and M - V = +-2), and creases
 * that stay flat (assignment F) are impossible at an interior vertex of a
 * flat-folded state - so any 'F' or 'U' crease makes the test inconclusive
 * rather than failed.
 *
 * Reference: J. Maekawa, published in K. Husimi (1989); J. Justin (1986).
 */
export function analyzeMaekawa(
  pattern: CreasePattern,
  v: VertexTopology,
): MaekawaResult {
  let mountains = 0;
  let valleys = 0;
  let flats = 0;
  let unassigned = 0;
  for (const e of v.edges) {
    switch (pattern.edgesAssignment[e]) {
      case 'M':
        mountains++;
        break;
      case 'V':
        valleys++;
        break;
      case 'F':
        flats++;
        break;
      default:
        unassigned++;
        break;
    }
  }
  const difference = mountains - valleys;
  const base: MaekawaResult = {
    vertex: v.index,
    degree: v.degree,
    applicable: false,
    mountains,
    valleys,
    flats,
    unassigned,
    difference,
    satisfied: false,
  };
  if (!v.interior) return { ...base, notApplicableReason: 'boundary-vertex' };
  if (v.degree < 4) return { ...base, notApplicableReason: 'degree-too-low' };
  if (unassigned > 0 || flats > 0) {
    return { ...base, notApplicableReason: 'unassigned-creases' };
  }
  return { ...base, applicable: true, satisfied: Math.abs(difference) === 2 };
}
